
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateInmopublicUserMutation } from '../inmopublicUsersApi';
import { UserPlus, Copy, Check } from 'lucide-react';
import { createUserSchema, type CreateUserFormData, type CreateUserResponse } from './CreateUserDialog.types';

export const CreateUserDialog = () => {
  const [open, setOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreateUserResponse | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const [createUser, { isLoading }] = useCreateInmopublicUserMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      profileImage: '',
    },
  });

  const onSubmit = async (data: CreateUserFormData) => {
    try {
      const submitData = {
        email: data.email.trim(),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        phone: data.phone ? Number(data.phone) : undefined,
        profileImage: data.profileImage?.trim() || undefined,
      };

      const result = await createUser(submitData).unwrap();
      setCreatedUser(result);
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (error?.data?.message?.includes('email already exists')) {
        setError('email', { message: 'User with this email already exists' });
      } else {
        setError('email', { message: 'Error creating user. Please try again.' });
      }
    }
  };

  const handleCopyPassword = async () => {
    if (createdUser?.password) {
      try {
        await navigator.clipboard.writeText(createdUser.password);
        setPasswordCopied(true);
        setTimeout(() => setPasswordCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy password:', error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    reset();
    setCreatedUser(null);
    setPasswordCopied(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className='bg-blue-600 hover:bg-blue-700 text-white'>
          <UserPlus className='h-4 w-4 mr-2' />
          Create User
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px] bg-slate-800 border-slate-700'>
        <DialogHeader>
          <DialogTitle className='text-white'>
            {createdUser ? 'User Created Successfully' : 'Create New User'}
          </DialogTitle>
        </DialogHeader>

        {createdUser ? (
          <div className='space-y-6'>
            <div className='bg-green-900/20 border border-green-700 rounded-lg p-4'>
              <h3 className='text-green-400 font-medium mb-2'>User Created Successfully!</h3>
              <div className='space-y-2 text-sm'>
                <p><span className='text-slate-400'>Name:</span> <span className='text-white'>{createdUser.user.firstName} {createdUser.user.lastName}</span></p>
                <p><span className='text-slate-400'>Email:</span> <span className='text-white'>{createdUser.user.email}</span></p>
                {createdUser.user.phone && (
                  <p><span className='text-slate-400'>Phone:</span> <span className='text-white'>{createdUser.user.phone}</span></p>
                )}
              </div>
            </div>

            <div className='bg-yellow-900/20 border border-yellow-700 rounded-lg p-4'>
              <div className='flex items-center justify-between mb-2'>
                <h3 className='text-yellow-400 font-medium'>Generated Password</h3>
                <Button
                  onClick={handleCopyPassword}
                  size='sm'
                  variant='outline'
                  className='text-yellow-400 border-yellow-400 hover:bg-yellow-400 hover:text-black'
                >
                  {passwordCopied ? (
                    <>
                      <Check className='h-4 w-4 mr-1' />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className='h-4 w-4 mr-1' />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <div className='bg-slate-700 p-3 rounded border font-mono text-sm text-white break-all'>
                {createdUser.password}
              </div>
              <p className='text-yellow-300 text-xs mt-2'>
                ⚠️ Please save this password securely. It will not be shown again.
              </p>
            </div>

            <div className='flex justify-end'>
              <Button onClick={handleClose} className='bg-blue-600 hover:bg-blue-700 text-white'>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='email' className='text-slate-300'>Email *</Label>
              <Input
                id='email'
                type='email'
                {...register('email')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='user@example.com'
              />
              {errors.email && <p className='text-red-400 text-sm'>{errors.email.message}</p>}
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='firstName' className='text-slate-300'>First Name *</Label>
                <Input
                  id='firstName'
                  {...register('firstName')}
                  className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                  placeholder='John'
                />
                {errors.firstName && <p className='text-red-400 text-sm'>{errors.firstName.message}</p>}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='lastName' className='text-slate-300'>Last Name *</Label>
                <Input
                  id='lastName'
                  {...register('lastName')}
                  className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                  placeholder='Doe'
                />
                {errors.lastName && <p className='text-red-400 text-sm'>{errors.lastName.message}</p>}
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='phone' className='text-slate-300'>Phone Number</Label>
              <Input
                id='phone'
                type='number'
                {...register('phone')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='1234567890'
              />
              {errors.phone && <p className='text-red-400 text-sm'>{errors.phone.message}</p>}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='profileImage' className='text-slate-300'>Profile Image URL</Label>
              <Input
                id='profileImage'
                {...register('profileImage')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='https://example.com/image.jpg'
              />
              {errors.profileImage && <p className='text-red-400 text-sm'>{errors.profileImage.message}</p>}
            </div>

            <div className='flex justify-end space-x-2 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                disabled={isLoading}
                className='border-slate-600 text-slate-300 hover:bg-slate-700'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={isLoading}
                className='bg-blue-600 hover:bg-blue-700 text-white'
              >
                {isLoading ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
